import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { DataTable, type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { toast } from '../../lib/toast';

type CsvRow = {
  sku?: string;
  ean?: string;
  stock_actual?: number | string | null;
  stock_df?: number | string | null;
  unidades_vendidas?: number | string | null;
  fecha?: string;
};

type PreviewItem = {
  row_index: number;
  matched: boolean;
  producto_id?: string;
  producto_nombre?: string;
  sku_input?: string;
  ean_input?: string;
  fecha: string;
  stock_actual: number | null;
  stock_df: number | null;
  unidades_vendidas: number | null;
  match_reason?: 'sku' | 'ean' | 'none';
  previous?: { stock_actual: number | null; stock_df: number | null; unidades_vendidas: number | null };
};

type PreviewResponse = {
  preview: PreviewItem[];
  summary: { total: number; matched: number; unmatched: number };
};

function parseCsv(text: string): CsvRow[] {
  const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const headers = parseRow(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = parseRow(line);
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = (cells[i] ?? '').trim();
    });
    return row as unknown as CsvRow;
  });
}

function parseRow(line: string): string[] {
  // Soporta comas y punto y coma como separador. Sin manejo completo de comillas anidadas.
  const sep = line.includes(';') && !line.includes(',') ? ';' : ',';
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' && (i === 0 || line[i - 1] !== '\\')) {
      inQuotes = !inQuotes;
      continue;
    }
    if (c === sep && !inQuotes) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += c;
  }
  out.push(cur);
  return out;
}

export function StockImport() {
  const [csv, setCsv] = useState('');
  const [fechaDefault, setFechaDefault] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [committed, setCommitted] = useState<{ inserted: number; skipped: number } | null>(null);

  const previewMutation = useMutation({
    mutationFn: (rows: CsvRow[]) => api.post<PreviewResponse>('/stock/preview', { rows, fecha_default: fechaDefault }),
    onSuccess: (data) => {
      setPreview(data);
      setCommitted(null);
    },
  });

  const commit = useMutation({
    mutationFn: () => api.post<{ inserted: number; skipped: number }>('/stock/commit', { items: preview?.preview ?? [] }),
    onSuccess: (data) => {
      setCommitted(data);
      setPreview(null);
      setCsv('');
    },
  });

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.readAsText(file, 'utf-8');
  };

  const cargarPreview = () => {
    const rows = parseCsv(csv);
    if (rows.length === 0) {
      toast.info('No hay filas que importar');
      return;
    }
    previewMutation.mutate(rows);
  };

  const columns: Column<PreviewItem>[] = [
    {
      key: 'matched',
      label: 'Match',
      render: (r) => r.matched ? <Badge color="#10b981">{r.match_reason?.toUpperCase()}</Badge> : <Badge color="#ef4444">No</Badge>,
      sortValue: (r) => (r.matched ? 1 : 0),
    },
    { key: 'sku_input', label: 'SKU', render: (r) => <span className="font-mono text-xs">{r.sku_input ?? '—'}</span>, sortValue: (r) => r.sku_input ?? '' },
    { key: 'ean_input', label: 'EAN', render: (r) => <span className="font-mono text-xs">{r.ean_input ?? '—'}</span>, sortValue: (r) => r.ean_input ?? '' },
    { key: 'producto_nombre', label: 'Producto', sortValue: (r) => r.producto_nombre ?? '' },
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.date(r.fecha), sortValue: (r) => r.fecha },
    {
      key: 'stock_actual',
      label: 'Stock actual',
      align: 'right',
      render: (r) => (
        <span>
          {fmt.number(r.stock_actual)}
          {r.previous && (
            <span className="ml-1 text-xs text-slate-400">
              (antes {fmt.number(r.previous.stock_actual)})
            </span>
          )}
        </span>
      ),
      sortValue: (r) => Number(r.stock_actual),
    },
    { key: 'stock_df', label: 'Stock DF', align: 'right', render: (r) => fmt.number(r.stock_df), sortValue: (r) => Number(r.stock_df) },
    { key: 'unidades_vendidas', label: 'Vendidas', align: 'right', render: (r) => fmt.number(r.unidades_vendidas), sortValue: (r) => Number(r.unidades_vendidas) },
  ];

  return (
    <div>
      <PageHeader
        title="Importador de stock"
        subtitle="Sube un CSV con SKU, EAN, stock actual, stock DF y unidades vendidas. Revisa el preview antes de aplicar."
      />

      <Card title="1. Sube el CSV o pega el contenido" description="Separador admitido: , o ;. Cabeceras esperadas: sku, ean, stock_actual, stock_df, unidades_vendidas, fecha (opcional).">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="block w-full max-w-md cursor-pointer rounded-md border border-slate-300 bg-white p-2 text-sm file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white"
            />
            <div className="flex items-center gap-2 text-sm">
              <label className="text-slate-600">Fecha por defecto:</label>
              <Input type="date" value={fechaDefault} onChange={(e) => setFechaDefault(e.target.value)} className="w-44" />
            </div>
          </div>
          <textarea
            className="block w-full min-h-[160px] rounded-md border border-slate-300 bg-white p-3 font-mono text-xs"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={`sku,ean,stock_actual,stock_df,unidades_vendidas\nGAME001,8412345678901,120,40,18`}
          />
          <div className="flex justify-end">
            <Button onClick={cargarPreview} disabled={!csv.trim() || previewMutation.isPending}>
              {previewMutation.isPending ? 'Procesando…' : 'Generar preview'}
            </Button>
          </div>
        </div>
      </Card>

      {preview && (
        <div className="mt-6">
          <Card
            title="2. Revisa el preview"
            description={`Filas: ${preview.summary.total} · Matched: ${preview.summary.matched} · No encontradas: ${preview.summary.unmatched}`}
            actions={
              <Button
                onClick={() => commit.mutate()}
                disabled={preview.summary.matched === 0 || commit.isPending}
              >
                Aplicar {preview.summary.matched} fila(s)
              </Button>
            }
          >
            <DataTable<PreviewItem>
              columns={columns}
              rows={preview.preview}
              rowKey={(r) => String(r.row_index)}
              defaultSort={{ key: 'matched' }}
              searchPlaceholder="Buscar en preview…"
            />
          </Card>
        </div>
      )}

      {committed && (
        <div className="mt-6">
          <Card>
            <p className="text-sm">
              ✅ Importación aplicada. <strong>{committed.inserted}</strong> snapshot(s) insertado(s).
              {committed.skipped > 0 && ` Saltadas: ${committed.skipped} (sin match en BD).`}
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
